import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IdeozLoading, NotFoundState } from '@ideoz/ui';
import {
  projectService,
  useStartups,
  useDiscussionRoom,
  useDiscussionConversations,
  useInvalidateDiscussionConversations,
} from '@ideoz/core';

/**
 * Discussion Room entry resolver — `/:startupSlug/discussions`.
 *
 * Mirrors ScopeConversationResolver: decides which conversation to open before
 * the chat workspace mounts, then redirects (replace).
 *   - Room has conversations → open the most-recent one.
 *   - Empty room → create the first conversation (Nest-owned) and open it.
 *
 * Unlike scopes, discussion conversations are created by Nest (not an AI shell),
 * so there is no `…/new` auto-prompt route — the founder lands on a real id.
 */
export default function DiscussionResolver(): JSX.Element {
  const { startupSlug } = useParams<{ startupSlug: string }>();
  const navigate = useNavigate();

  const {
    data: startups = [],
    isFetched: startupsFetched,
    isFetching: startupsFetching,
    isError: startupsError,
  } = useStartups();
  const matchedStartup = startups.find((s) => s.slug === startupSlug) ?? null;
  const startupId = matchedStartup?.id ?? null;

  const { data: room } = useDiscussionRoom(startupId);
  const { data: convData, isSuccess: conversationsLoaded } = useDiscussionConversations(
    startupId,
    room?.id,
  );
  const invalidateConversations = useInvalidateDiscussionConversations();

  // Guard the one-time create so an in-flight navigation doesn't spawn a second
  // conversation on a re-render.
  const creatingRef = useRef(false);

  useEffect(() => {
    if (!startupSlug || !startupId || !room || !conversationsLoaded) return;
    const convs = convData ?? [];
    if (convs.length > 0) {
      navigate(`/${startupSlug}/discussions/${convs[0].id}`, { replace: true });
      return;
    }
    if (creatingRef.current) return;
    creatingRef.current = true;
    projectService
      .createDiscussionConversation(startupId, room.id)
      .then(async (conv) => {
        await invalidateConversations(startupId, room.id);
        navigate(`/${startupSlug}/discussions/${conv.id}`, { replace: true });
      })
      .catch((err) => {
        creatingRef.current = false;
        console.error('Failed to create discussion conversation:', err);
      });
  }, [
    startupSlug,
    startupId,
    room,
    conversationsLoaded,
    convData,
    navigate,
    invalidateConversations,
  ]);

  // 404 only once the startups query has settled (same guard as the workspace).
  if (startupsFetched && !startupsFetching && !startupsError && (!matchedStartup || !startupId)) {
    return (
      <NotFoundState
        description="Startup not found"
        actionLabel="Back to My Startups"
        onAction={() => navigate('/startups')}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <IdeozLoading size="lg" />
    </div>
  );
}
